// frontend/src/hooks/useSessionTimeout.jsx
import { useState, useEffect, useRef } from 'react';
import { sessionApi } from '../services/SessionApiService';

// Production timeout values - matches backend SESSION_COOKIE_AGE (60 minutes)
const WARNING_TIME = 55 * 60 * 1000;  // 55 minutes - show warning 5 min before logout
const LOGOUT_TIME = 60 * 60 * 1000;   // 60 minutes - matches backend session timeout
const KEEPALIVE_THROTTLE = 60 * 1000; // Max 1 keepalive per minute


export const useSessionTimeout = (isAuthenticated, onLogout, userContext) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(LOGOUT_TIME - WARNING_TIME); // Initialize with remaining time
  
  const warningTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countdownTimer = useRef(null);
  // Date.now() in useRef initializers — see AuthContext.jsx for rationale.
  // eslint-disable-next-line react-hooks/purity
  const lastActivity = useRef(Date.now());
  // eslint-disable-next-line react-hooks/purity
  const lastKeepalive = useRef(Date.now());
  const showWarningRef = useRef(false);

  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  // Disable timeout for "remember me" sessions
  const isRememberMe = userContext?.session?.rememberMe === true;

  // Send keepalive to backend
  const sendKeepalive = async () => {
    // Update timestamp BEFORE the await to prevent concurrent calls
    // (multiple mousemove events fire synchronously before any await resolves)
    lastKeepalive.current = Date.now();
    // Activity handlers fire this without awaiting it, so its failures are
    // logged here rather than left as an unhandled rejection.
    try {
      const response = await sessionApi.post('/auth/keepalive/');
      if (response.status === 401 || response.status === 403) {
        onLogout();
      } else if (!response.ok) {
        console.error('Session keepalive failed:', response.status);
      }
    } catch (error) {
      console.error('Session keepalive failed:', error);
    }
  };

  // Restarts the warning and logout countdowns. Sets no state itself, so the
  // effect below can start a run without a synchronous render cascade.
  const scheduleTimers = () => {
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);
    clearInterval(countdownTimer.current);
    lastActivity.current = Date.now();

    if (!isAuthenticated) return;

    // Warning at WARNING_TIME
    warningTimer.current = setTimeout(() => {
      setShowWarning(true);
      setTimeLeft(LOGOUT_TIME - WARNING_TIME); // Set initial countdown time
      
      const start = Date.now();
      countdownTimer.current = setInterval(() => {
        const elapsed = Date.now() - start;
        const remaining = (LOGOUT_TIME - WARNING_TIME) - elapsed;
        setTimeLeft(remaining > 0 ? remaining : 0);
      }, 1000);
    }, WARNING_TIME);

    // Logout at LOGOUT_TIME
    logoutTimer.current = setTimeout(() => {
      setShowWarning(false);
      onLogout();
    }, LOGOUT_TIME);
  };

  const resetTimer = () => {
    if (isRememberMe) return;
    setShowWarning(false);
    setTimeLeft(LOGOUT_TIME - WARNING_TIME);
    scheduleTimers();
  };

  useEffect(() => {
    if (!isAuthenticated || isRememberMe) {
      return;
    }

    scheduleTimers();

    // Handle user activity
    const handleActivity = () => {
      if (showWarningRef.current) return;
      
      const now = Date.now();
      
      // Send keepalive if throttle period passed
      if (now - lastKeepalive.current >= KEEPALIVE_THROTTLE) {
        sendKeepalive();
      }
      
      resetTimer();
    };

    // Handle tab visibility
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const elapsed = Date.now() - lastActivity.current;
        
        if (elapsed >= LOGOUT_TIME) {
          onLogout();
        } else if (elapsed >= WARNING_TIME && !showWarningRef.current) {
          setShowWarning(true);
          
          // Calculate remaining time correctly
          const remaining = LOGOUT_TIME - elapsed;
          setTimeLeft(remaining);
          
          const start = Date.now();
          countdownTimer.current = setInterval(() => {
            const e = Date.now() - start;
            const r = remaining - e;
            setTimeLeft(r > 0 ? r : 0);
          }, 1000);
        }
      }
    };

    document.addEventListener('click', handleActivity);
    document.addEventListener('keydown', handleActivity);
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('click', handleActivity);
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(warningTimer.current);
      clearTimeout(logoutTimer.current);
      clearInterval(countdownTimer.current);
      // The warning belongs to this run of the timers, so ending the run
      // clears it — it never carries into the next sign-in.
      setShowWarning(false);
      setTimeLeft(LOGOUT_TIME - WARNING_TIME);
    };
    // scheduleTimers, resetTimer and sendKeepalive are rebuilt every render
    // but read only refs and the three props listed here, so listing them
    // would re-attach the document-wide listeners on every render for no
    // change in behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, onLogout, isRememberMe]);

  // API activity notification
  const notifyApiActivity = () => {
    if (isAuthenticated && !showWarningRef.current && !isRememberMe) {
      resetTimer();
    }
  };

  return { showWarning, timeLeft, resetTimer, notifyApiActivity };
};

export const formatTime = (ms) => {
  const sec = Math.ceil(ms / 1000);
  return `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, '0')}`;
};